import type { ShapedComponent } from '@repo/api-contracts';
import { areaShapePathOf, type FlatView, surfaceBoxOf } from '@/features/design-editor';
import { cn } from '@/shared/ui';

interface AreaShapeOutlineProps {
  readonly component: ShapedComponent & { readonly id: string };
  readonly view: FlatView;
  readonly active: boolean;
  /** 이 영역에서 노드를 골랐거나 포인터가 올라와 있을 때 — 편집 중인 영역이면 테두리를 진하게 보인다. */
  readonly emphasized?: boolean;
  /**
   * outline(기본) — 레이어 위에 겹치는 테두리(평소 옅은 실선, 편집 중엔 점선). fill — 사진이 없을 때 레이어 아래에 까는 모양대로의 흰 바탕
   * (사각형 상자의 bg-background·shadow-sm 대신).
   */
  readonly variant?: 'outline' | 'fill';
}

/** 인쇄 영역 모양을 따라 그리는 테두리(또는 흰 바탕). 사각형이 아닌 영역도 모양대로 보인다. */
export function AreaShapeOutline({
  component,
  view,
  active,
  emphasized = false,
  variant = 'outline',
}: AreaShapeOutlineProps) {
  const isFill = variant === 'fill';
  const strong = active && emphasized;
  const dashed = active;
  return (
    <svg
      aria-hidden
      className={cn('pointer-events-none absolute overflow-visible', isFill && 'drop-shadow-sm')}
      style={surfaceBoxOf(view, component)}
    >
      {isFill ? (
        <path
          data-testid={`area-fill-${component.id}`}
          d={areaShapePathOf(component, view.pxPerMmX, view.pxPerMmY)}
          fillRule="evenodd"
          className="fill-background"
        />
      ) : (
        <path
          data-testid={`area-shape-${component.id}`}
          d={areaShapePathOf(component, view.pxPerMmX, view.pxPerMmY)}
          fill="none"
          fillRule="evenodd"
          strokeWidth={1}
          strokeDasharray={dashed ? '4 3' : undefined}
          vectorEffect="non-scaling-stroke"
          className={cn(
            !active && 'stroke-foreground/15',
            active && !strong && 'stroke-foreground/35',
            strong && 'stroke-foreground/60',
          )}
        />
      )}
    </svg>
  );
}
